import { useQuery } from '@apollo/client';
import { filterProductsQuery } from '@components/components/Filters/FiltersQuery';
import { FilterProduct } from '@components/renderers/ContentfulPagesRenderer/components/CentraProducts/CentraProducts';
import { useRouter } from 'next/router';

export const buildBaseFilters = (props: any, query: any) => {
  const uri = query?.uri?.join('/');

  if (props.isSearch) {
    const searchText = Array.isArray(query.q) ? query.q[0] : query?.q || '';

    return {
      search: searchText,
    };
  }

  if (props.category) {
    return {
      uri: props.category,
    };
  }

  if (!uri) {
    return {};
  }

  return {
    uri,
  };
};

export const adoptForCentra = (filters: Record<string, string | string[]>) => {
  if (Array.isArray(filters?.search)) {
    return {
      ...filters,
      search: filters.search.join(','),
    };
  }

  return filters;
};

export const buildQueryVars = query => {
  const baseQueryVariables = {
    filters: {
      limit: 0,
      skipFirst: 0,
      ...(query?.uri && { uri: (query.uri as string[]).join('/') }),
    },
  };
  // @ts-expect-error
  const { q, ...baseFilters } = buildBaseFilters({ isSearch: !!query?.q?.length }, query);
  const selectedFilters = buildFromUrl(baseFilters);

  return {
    ...baseQueryVariables,
    filters: { ...baseFilters, ...adoptForCentra(selectedFilters) },
    limit: 200,
    baseFilters,
    hasUrlFilters: true,
  };
};

const buildFromUrl = (query: any) => {
  return Object.keys(query)
    .filter(key => key !== 'uri')
    .map(key => {
      if (!query || !query[key]) {
        return null;
      }
      return {
        [key]: query[key].split(','),
      };
    })
    .reduce((acc, currentValue) => (currentValue ? { ...acc, ...currentValue } : acc), {});
};

const getFilteredProducts = (
  products: FilterProduct[],
  { sizes = [], lengths = [], colors = [], sort = [], types = [] }: Record<string, any>,
) => {
  const filterProducts = (product: FilterProduct) => {
    if (sizes.length === 0 && lengths.length === 0 && colors.length === 0 && types.length === 0) {
      return true;
    }

    const { items, isRaffle, swatch, fit } = product;
    const stockItems = items.filter(item => isRaffle || item.stock !== 'no');
    const hasSizes = stockItems.some(({ name }) => {
      const isCombinedSize = name.match(/\d+x\d+/);
      if (name) {
        const finalName = name.trim().replaceAll(' ', '-').toLowerCase();
        if (isCombinedSize) {
          const [size, length] = finalName.split('x');
          return sizes.includes(size) || lengths.includes(length);
        } else {
          return sizes.includes(finalName) || lengths.includes(finalName);
        }
      }
      return false;
    });
    const hasColors = swatch && colors.includes(swatch.desc.trim().replaceAll(' ', '-').toLowerCase());
    const hasType = fit && types.includes(fit.toLowerCase());
    return hasSizes || hasColors || hasType;
  };

  const sortProducts = (a: FilterProduct, b: FilterProduct) => {
    if (sort.length === 0) {
      return 0;
    }
    const [sortValue] = sort;
    if (sortValue === 'a-z') {
      return a.name.localeCompare(b.name);
    }
    if (sortValue === 'z-a') {
      return b.name.localeCompare(a.name);
    }
    if (sortValue === 'price-low-to-hight') {
      if (!a.priceAsNumber || !b.priceAsNumber) {
        return -1;
      }
      if (a.priceAsNumber < b.priceAsNumber) {
        return -1;
      }
      if (a.priceAsNumber > b.priceAsNumber) {
        return 1;
      }
      return 0;
    }
    if (sortValue === 'price-high-to-low') {
      if (!a.priceAsNumber || !b.priceAsNumber) {
        return -1;
      }
      if (a.priceAsNumber > b.priceAsNumber) {
        return -1;
      }
      if (a.priceAsNumber < b.priceAsNumber) {
        return 1;
      }
      return 0;
    }
    if (sortValue === 'popular') {
      return 0;
    }
    return 0;
  };

  return [...products.filter(filterProducts).sort(sortProducts)];
};

export const useProducts = (props: any) => {
  const { query } = useRouter();
  const { sizes, lengths, colors, sort, types, ...rest } = query;
  const selectedFilters = buildFromUrl({ sizes, lengths, colors, sort, types });
  const queryVariables = buildQueryVars(rest);

  const graphqlResponse = useQuery(filterProductsQuery, {
    variables: queryVariables,
  });

  const { data } = graphqlResponse;
  const products = data?.filterProducts ?? { products: [] };

  const fp = {
    ...products,
    products: products.products.filter((product: { isRaffle: any }) => !product.isRaffle),
  };

  return {
    ...graphqlResponse,
    products: fp.products,
    filter: data?.fullFilters?.filter,
    productCount: fp.productCount,
    selectedFilters,
    filteredProducts: getFilteredProducts(fp.products, selectedFilters),
  };
};
